import { KEY_TABLE_TEXT_PX } from './exportLabelKeyTable'
import {
  alongPathSizePx,
  exportLabelSizePx,
  IExportLabelInput,
} from './exportLabelScene'
import {
  CLUSTER_MARKER_TEXT_PX,
  EXPORT_LABEL_KIND_ORDER,
  ExportLabelKind,
  LABEL_FLOOR_PX,
  PT_PER_CSS_PX,
} from './exportLabelTypes'
import { PAGE_BAND_TITLE_PX } from './exportPageBand'
import {
  IExportReadDistance,
  READ_SCALE_BY_SHEET,
  readScaleDistance,
} from './exportReadScale'
import { LOCATION_LABEL, MARKUP_LABEL, PIN_LABEL } from './labelLayerStyle'
import {
  CSS_PX_PER_INCH,
  ExportPaperPreset,
  METRIC_PAPER_PRESETS,
  MM_PER_INCH,
} from './mapExportPaper'
import { MARKER_SPEC, PIN_SPEC } from './markerGeometry'

// Markup text: the text markups themselves, drawn into the map.
export type ExportTextKind = ExportLabelKind | 'markupText' | 'key' | 'band'

export const EXPORT_TEXT_KIND_ORDER: ExportTextKind[] = [
  ...EXPORT_LABEL_KIND_ORDER,
  'markupText',
  'key',
  'band',
]

// The text kinds a Print row sets a printed size for: all but Markup text, which is part of the map
// and follows its zoom.
export type ExportPrintTextKind = Exclude<ExportTextKind, 'markupText'>

// The graphics sized apart from the names: Workflow pins and Location icons.
export type ExportSymbolKind = 'workflowPin' | 'location'

export const EXPORT_SYMBOL_KIND_ORDER: ExportSymbolKind[] = [
  'workflowPin',
  'location',
]

// A row Print sizes in print units.
export type ExportPrintRow =
  | { type: 'text'; kind: ExportPrintTextKind }
  | { type: 'symbol'; kind: ExportSymbolKind }

// What held a scale off the one asked for: the smallest size that reads at this distance, or
// the largest that fits the paper.
export type ExportScaleBinding = 'floor' | 'fit' | null

// The key, its numbers, the title and legend are read up close, as a poster's small print, so they stop
// growing at N 1.5.
const REFERENCE_FACTOR_MAX = 1.5
// The smallest text the key and the band set at ×1.
const REFERENCE_TEXT_MIN_PX = 10
// From about 1.07 m every name reads at one distance: it fits at its full size or is numbered.
const NAMES_FIXED_FROM = 2

// How much larger pins and Location icons print for the reading scale: with the names while the
// sheet is held, ×1 to N 1.2, then ×1.5 at N 1.5 and ×2 from N 2; between stops, a straight line.
const SYMBOL_STOPS: Array<[number, number]> = [
  [0, 0],
  [1, 1],
  [1.2, 1],
  [1.5, 1.5],
  [2, 2],
]

function symbolCurve(readScale: number): number {
  const upper = SYMBOL_STOPS.findIndex(([at]) => readScale <= at)
  if (upper === -1) return SYMBOL_STOPS[SYMBOL_STOPS.length - 1][1]
  if (upper === 0) return SYMBOL_STOPS[0][1]
  const [fromAt, from] = SYMBOL_STOPS[upper - 1]
  const [toAt, to] = SYMBOL_STOPS[upper]
  return from + ((readScale - fromAt) / (toAt - fromAt)) * (to - from)
}

export type ExportReadZone = 'inHand' | 'upClose' | 'acrossRoom'

// Where the sizes change their rule: pins grow once the sheet leaves the hand, names stop shrinking
// across a room.
export const readScaleZone = (readScale: number): ExportReadZone => {
  if (symbolCurve(readScale) <= 1) return 'inHand'
  return readScale < NAMES_FIXED_FROM ? 'upClose' : 'acrossRoom'
}

export interface IExportTextScaleInput {
  isPaper: boolean
  paper: ExportPaperPreset
  // N: the easy-reading distance over a ×1 Workflow name's own. Print only.
  readScale: number
  // As on map draws the map's own sizes, so Overall size and Adjust by type do not apply, and the
  // page elements read at the paper's Standard.
  isReadable: boolean
  // Overall size, on Screen only: on Print the reading scale sets the size.
  trim: number
}

// Print text keeps its set size at any zoom, as a reference scale does; Screen matches the map.
export const textZoomScale = (isPaper: boolean, zoomScale: number): number =>
  isPaper ? 1 : zoomScale

// Screen has no physical size, so no distance; As on map hides the distance, so its Standard.
const nameFactor = ({
  isPaper,
  paper,
  readScale,
  isReadable,
}: IExportTextScaleInput): number => {
  if (!isPaper) return 1
  return isReadable ? readScale : READ_SCALE_BY_SHEET[paper].standard
}

const referenceFactor = (input: IExportTextScaleInput): number =>
  Math.min(nameFactor(input), REFERENCE_FACTOR_MAX)

// As on map hangs the map's own labels at fixed offsets from the icons, so they keep their size.
const symbolFactor = (input: IExportTextScaleInput): number =>
  input.isPaper && input.isReadable ? symbolCurve(input.readScale) : 1

// The distance's factor times each kind's own percent, and Overall size on Screen; As on map, the
// map's own sizes.
export function resolveSymbolScales(
  input: IExportTextScaleInput,
  percents: Record<ExportSymbolKind, number>,
): Record<ExportSymbolKind, number> {
  const factor = symbolFactor(input)
  const scaleFor = (kind: ExportSymbolKind): number =>
    factor * userScale(input, percents[kind])
  return {
    workflowPin: scaleFor('workflowPin'),
    location: scaleFor('location'),
  }
}

// Print pins and icons keep their set size at any zoom, as Print text does; Screen matches the map,
// and As on map keeps the map's own sizes.
export const isSymbolSizeFixed = ({
  isPaper,
  isReadable,
}: IExportTextScaleInput): boolean => isPaper && isReadable

export const symbolZoomScale = (
  input: IExportTextScaleInput,
  zoomScale: number,
): number => (isSymbolSizeFixed(input) ? 1 : zoomScale)

// The kind's own percent, times Overall size on Screen.
const userScale = (
  { isPaper, isReadable, trim }: IExportTextScaleInput,
  kindPercent: number,
): number => (isReadable ? (isPaper ? 1 : trim / 100) * (kindPercent / 100) : 1)

// Markup text is part of the map, so no distance, floor or cap: at 100% it draws exactly as the
// map does, and As on map always does.
export const resolveMarkupTextScale = (
  input: IExportTextScaleInput,
  percent: number,
): number => userScale(input, percent)

function referenceScale(
  factor: number,
  scale: number,
  fit: number,
): { scale: number; binding: ExportScaleBinding } {
  const floor = (LABEL_FLOOR_PX * Math.max(1, factor)) / REFERENCE_TEXT_MIN_PX
  const wanted = factor * scale
  if (fit < Math.max(floor, wanted)) {
    return { scale: fit, binding: 'fit' }
  }
  return wanted < floor
    ? { scale: floor, binding: 'floor' }
    : { scale: wanted, binding: null }
}

// Needs only the page width through `bandFit`, so the sheet can take the band's height from it.
export function resolveBandScale(
  input: IExportTextScaleInput & { bandPercent: number; bandFit: number },
): { bandScale: number; binding: ExportScaleBinding } {
  const { scale, binding } = referenceScale(
    referenceFactor(input),
    userScale(input, input.bandPercent),
    input.bandFit,
  )
  return { bandScale: scale, binding }
}

export interface IExportTextScales {
  namePercent: Record<ExportLabelKind, number>
  nameFloorPx: number
  canShrinkNames: boolean
  keyScale: number
  keyBinding: ExportScaleBinding
  // The key's numbers on the map: the key's scale, grown with the symbols.
  markScale: number
  // The group disks, which stand in for pins: Workflow pins' size, never below a readable count.
  diskScale: number
}

interface IExportKindScaleInput extends IExportTextScaleInput {
  kindPercent: Record<ExportTextKind, number>
}

const keyReference = (
  input: IExportKindScaleInput & { keyFit: number },
): { scale: number; binding: ExportScaleBinding } =>
  referenceScale(
    referenceFactor(input),
    userScale(input, input.kindPercent.key),
    input.keyFit,
  )

function nameScales(
  input: IExportKindScaleInput,
): Pick<IExportTextScales, 'namePercent' | 'nameFloorPx'> {
  const factor = nameFactor(input)
  const percentFor = (kind: ExportLabelKind): number =>
    100 * factor * userScale(input, input.kindPercent[kind])
  return {
    namePercent: {
      workflowPin: percentFor('workflowPin'),
      location: percentFor('location'),
      markup: percentFor('markup'),
    },
    nameFloorPx: LABEL_FLOOR_PX * Math.max(1, factor),
  }
}

// How far the export's names read at this distance's own size: Workflow names, else the Location
// tier. Adjust by type does not move it, so the distance row reads its own value.
export function resolveReadDistance(
  input: IExportTextScaleInput & { hasWorkflowNames: boolean },
): IExportReadDistance {
  const tier = input.hasWorkflowNames ? PIN_LABEL : LOCATION_LABEL
  return readScaleDistance(
    (tier.sizePx * nameFactor(input)) / PIN_LABEL.sizePx,
    METRIC_PAPER_PRESETS.includes(input.paper),
  )
}

export function resolveExportTextScales(
  input: IExportKindScaleInput & {
    keyFit: number
    symbolPercent: Record<ExportSymbolKind, number>
  },
): IExportTextScales {
  const key = keyReference(input)
  const names = nameScales(input)
  // The count is read as the key is and the disk is seen as the pins are, so it takes the larger.
  const diskFactor = Math.max(referenceFactor(input), symbolFactor(input))
  return {
    ...names,
    canShrinkNames: nameFactor(input) < NAMES_FIXED_FROM,
    keyScale: key.scale,
    keyBinding: key.binding,
    markScale:
      key.scale * Math.max(1, symbolFactor(input) / referenceFactor(input)),
    diskScale: Math.max(
      diskFactor * userScale(input, input.symbolPercent.workflowPin),
      names.nameFloorPx / CLUSTER_MARKER_TEXT_PX,
    ),
  }
}

// The Mapbox credits and the StruxHub logo grow as the key does with distance, never below their
// own size, whatever Overall size is. Apart from the text scales, so the key's room can follow the
// logo without the key's own fit depending on it.
export const resolveBrandMarkScale = (input: IExportTextScaleInput): number =>
  Math.max(1, referenceFactor(input))

export interface IExportPrintedLength {
  value: number
  unit: 'mm' | 'in'
}

const SYMBOL_OWN_PX: Record<ExportSymbolKind, number> = {
  workflowPin: PIN_SPEC.boxPx,
  location: MARKER_SPEC.outerSize,
}

// The size each kind prints at when Optimized, the one mode that sizes them: a pin's box, an
// icon's circle, in whole millimetres on metric paper, else hundredths of an inch.
export function resolveSymbolPrintSizes(
  input: IExportTextScaleInput,
  percents: Record<ExportSymbolKind, number>,
): Record<ExportSymbolKind, IExportPrintedLength> {
  const scales = resolveSymbolScales(
    { ...input, isPaper: true, isReadable: true },
    percents,
  )
  const isMetric = METRIC_PAPER_PRESETS.includes(input.paper)
  const lengthOf = (kind: ExportSymbolKind): IExportPrintedLength => {
    const inches = (SYMBOL_OWN_PX[kind] * scales[kind]) / CSS_PX_PER_INCH
    return isMetric
      ? { value: Math.round(inches * MM_PER_INCH), unit: 'mm' }
      : { value: Math.round(inches * 100) / 100, unit: 'in' }
  }
  return {
    workflowPin: lengthOf('workflowPin'),
    location: lengthOf('location'),
  }
}

// What a Print row prints at Auto, at reading scale N, before any floor or fit: its standard size
// times the distance's factor. Location labels read at the standard tier, so a set size means the
// same thing wherever the frame moves; labels with their own sizes keep their ratio to it.
export function printAutoPx(row: ExportPrintRow, readScale: number): number {
  if (row.type === 'symbol') {
    return SYMBOL_OWN_PX[row.kind] * symbolCurve(readScale)
  }
  const reference = Math.min(readScale, REFERENCE_FACTOR_MAX)
  switch (row.kind) {
    case 'workflowPin':
      return PIN_LABEL.sizePx * readScale
    case 'location':
      return LOCATION_LABEL.sizePx * readScale
    case 'markup':
      return alongPathSizePx(MARKUP_LABEL.sizePx * readScale, 0)
    case 'key':
      return KEY_TABLE_TEXT_PX * reference
    default:
      return PAGE_BAND_TITLE_PX * reference
  }
}

// What Adjust by type holds. Print rows hold a printed size each, absent while the row follows the
// distance (Auto); Screen rows and Markup text hold a percent.
export interface IExportKindValues {
  textPercent: Record<ExportTextKind, number>
  symbolPercent: Record<ExportSymbolKind, number>
  textPx: Partial<Record<ExportPrintTextKind, number>>
  symbolPx: Partial<Record<ExportSymbolKind, number>>
}

// Each kind's percent at this distance: on Print, the one that prints a set size, else 100; on
// Screen, the row's own. Floors and fits still hold what follows.
export function resolveKindPercents(
  input: IExportTextScaleInput,
  values: IExportKindValues,
): {
  text: Record<ExportTextKind, number>
  symbol: Record<ExportSymbolKind, number>
} {
  if (!input.isPaper) {
    return { text: values.textPercent, symbol: values.symbolPercent }
  }
  const percentOf = (row: ExportPrintRow, px: number | undefined): number =>
    px === undefined ? 100 : (100 * px) / printAutoPx(row, input.readScale)
  const text = { ...values.textPercent }
  EXPORT_TEXT_KIND_ORDER.forEach(kind => {
    if (kind !== 'markupText') {
      text[kind] = percentOf({ type: 'text', kind }, values.textPx[kind])
    }
  })
  return {
    text,
    symbol: {
      workflowPin: percentOf(
        { type: 'symbol', kind: 'workflowPin' },
        values.symbolPx.workflowPin,
      ),
      location: percentOf(
        { type: 'symbol', kind: 'location' },
        values.symbolPx.location,
      ),
    },
  }
}

// The font size most of the export's text markups are set in, the smaller on a tie.
export function commonMarkupTextPx(inputs: IExportLabelInput[]): number {
  const counts = new Map<number, number>()
  inputs.forEach(({ textBox }) => {
    if (textBox) {
      counts.set(textBox.fontSizePx, (counts.get(textBox.fontSizePx) ?? 0) + 1)
    }
  })
  let common = 0
  let commonCount = 0
  counts.forEach((count, size) => {
    if (count > commonCount || (count === commonCount && size < common)) {
      common = size
      commonCount = count
    }
  })
  return common
}

// The kinds the export sets: names some label carries, Markup text when a text markup is drawn,
// the key when listed, the band when shown.
export const printedTextKinds = (
  inputs: IExportLabelInput[],
  printed: { isKeyIncluded: boolean; arePageElementsShown: boolean },
): ExportTextKind[] =>
  EXPORT_TEXT_KIND_ORDER.filter(kind => {
    if (kind === 'key') return printed.isKeyIncluded
    if (kind === 'band') return printed.arePageElementsShown
    if (kind === 'markupText') return inputs.some(input => !!input.textBox)
    return inputs.some(input => input.kind === kind && !!input.text)
  })

export interface IExportTextKindSize {
  points: number
  binding: ExportScaleBinding
}

const halfPoints = (px: number): number =>
  Math.round(px * PT_PER_CSS_PX * 2) / 2

// What each kind prints at, to the half point, and what held it. Names: a Workflow and a Markup
// label at their tier, a Location at `locationPx`, each after the zoom scaling the scene applies.
// Markup text: the common text at `markupTextPx`, its size on the sheet as the map draws it. The
// key: its row text. The band: its title.
export function resolveTextKindSizes(
  input: IExportKindScaleInput & {
    keyFit: number
    bandFit: number
    locationPx: number
    markupTextPx: number
    textZoomScale: number
  },
): Record<ExportTextKind, IExportTextKindSize> {
  const { namePercent, nameFloorPx } = nameScales(input)
  const key = keyReference(input)
  const name = (sizePx: number): IExportTextKindSize => ({
    points: halfPoints(sizePx),
    binding: sizePx <= nameFloorPx ? 'floor' : null,
  })
  const nameSizePx = (kind: ExportLabelKind, ownPx: number): number =>
    exportLabelSizePx(
      ownPx * input.textZoomScale,
      namePercent[kind],
      nameFloorPx,
    )
  const { bandScale, binding: bandBinding } = resolveBandScale({
    ...input,
    bandPercent: input.kindPercent.band,
  })
  return {
    workflowPin: name(nameSizePx('workflowPin', PIN_LABEL.sizePx)),
    location: name(nameSizePx('location', input.locationPx)),
    markup: name(
      alongPathSizePx(nameSizePx('markup', MARKUP_LABEL.sizePx), nameFloorPx),
    ),
    markupText: {
      points: halfPoints(
        input.markupTextPx *
          resolveMarkupTextScale(input, input.kindPercent.markupText),
      ),
      binding: null,
    },
    key: {
      points: halfPoints(KEY_TABLE_TEXT_PX * key.scale),
      binding: key.binding,
    },
    band: {
      points: halfPoints(PAGE_BAND_TITLE_PX * bandScale),
      binding: bandBinding,
    },
  }
}
